import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "rowCheckbox", "batchForm", "batchIdsContainer", "batchLabel", "batchSelect", "perPage"]
  static values = {
    msgNoAction: { type: String, default: "작업을 선택해 주세요." },
    msgNoItems: { type: String, default: "선택된 %{label}이(가) 없습니다." },
    msgConfirmSaveOrder: { type: String, default: "%{count}개 %{label}의 우선순위를 저장하시겠습니까?" },
    msgConfirmActivate: { type: String, default: "%{count}개 %{label}을(를) 활성화하시겠습니까?" },
    msgConfirmDeactivate: { type: String, default: "%{count}개 %{label}을(를) 비활성화하시겠습니까?" },
    msgConfirmDelete: { type: String, default: "%{count}개 %{label}을(를) 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다." },
    msgDefaultLabel: { type: String, default: "항목" }
  }

  _interpolate(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, k) => vars[k] !== undefined ? vars[k] : "")
  }

  connect() {
    this.refreshCsrfToken()
    this.updateSelectedCount()
  }

  toggleSelectAll(event) {
    const checked = event.currentTarget.checked
    this.rowCheckboxTargets.forEach(cb => cb.checked = checked)
    this.updateSelectedCount()
  }

  updateSelectedCount() {
    const checked = this.rowCheckboxTargets.filter(cb => cb.checked)
    const total = this.rowCheckboxTargets.length

    // Update hidden inputs for batch form
    if (this.hasBatchIdsContainerTarget) {
      const fieldName = this.batchIdsContainerTarget.dataset.fieldName || "asset_ids[]"
      this.batchIdsContainerTarget.innerHTML = checked.map(cb => {
        const priorityInput = this.element.querySelector(`.banner-priority-input[data-banner-id="${cb.value}"]`)
        const priorityHtml = priorityInput ? `<input type="hidden" name="banner_priorities[${cb.value}]" value="${priorityInput.value}">` : ""
        return `<input type="hidden" name="${fieldName}" value="${cb.value}">${priorityHtml}`
      }).join("")
    }

    if (checked.length === 0) {
      if (this.hasSelectAllTarget) {
        this.selectAllTarget.checked = false
        this.selectAllTarget.indeterminate = false
        this.selectAllTarget.classList.remove("indeterminate")
      }
      if (this.hasBatchFormTarget) this.batchFormTarget.style.visibility = "hidden"
      if (this.hasBatchLabelTarget) {
        const max = parseInt(this.batchLabelTarget.dataset.max || "0")
        if (max > 0) {
          const color = total >= max ? "#DC2626" : "#111827"
          this.batchLabelTarget.innerHTML = `등록 <strong style="color: ${color};">${total}</strong> / ${max}개`
        } else {
          this.batchLabelTarget.innerHTML = `전체 <strong style="color: #111827;">${total}</strong>건`
        }
      }
    } else {
      if (this.hasBatchFormTarget) this.batchFormTarget.style.visibility = "visible"
      if (this.hasBatchLabelTarget) this.batchLabelTarget.innerHTML = `선택 <strong style="color: #4F46E5;">${checked.length}</strong>건`
      if (this.hasSelectAllTarget) {
        if (checked.length === total) {
          this.selectAllTarget.checked = true
          this.selectAllTarget.indeterminate = false
          this.selectAllTarget.classList.remove("indeterminate")
        } else {
          this.selectAllTarget.checked = false
          this.selectAllTarget.indeterminate = true
          this.selectAllTarget.classList.add("indeterminate")
        }
      }
    }
  }

  syncPriorities() {
    if (this.rowCheckboxTargets.some(cb => cb.checked)) {
      this.updateSelectedCount()
    }
  }

  submitBatchAction(event) {
    this.refreshCsrfToken()

    const checked = this.rowCheckboxTargets.filter(cb => cb.checked)
    const label = this.element.dataset.batchLabel || this.msgDefaultLabelValue
    if (checked.length === 0) {
      event.preventDefault()
      alert(this._interpolate(this.msgNoItemsValue, { label }))
      return
    }

    const action = this.hasBatchSelectTarget ? this.batchSelectTarget.value : ""
    let confirmMsg = ""
    const vars = { count: checked.length, label }

    if (action === "save_order") confirmMsg = this._interpolate(this.msgConfirmSaveOrderValue, vars)
    else if (action === "activate") confirmMsg = this._interpolate(this.msgConfirmActivateValue, vars)
    else if (action === "deactivate") confirmMsg = this._interpolate(this.msgConfirmDeactivateValue, vars)
    else if (action === "delete") confirmMsg = this._interpolate(this.msgConfirmDeleteValue, vars)
    else {
      event.preventDefault()
      alert(this.msgNoActionValue)
      return
    }

    if (!confirm(confirmMsg)) event.preventDefault()
  }

  refreshCsrfToken() {
    if (!this.hasBatchFormTarget) return

    const token = document.querySelector("meta[name='csrf-token']")?.content
    const param = document.querySelector("meta[name='csrf-param']")?.content || "authenticity_token"
    if (!token) return

    let input = this.batchFormTarget.querySelector(`input[name="${param}"]`)
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = param
      this.batchFormTarget.prepend(input)
    }
    input.value = token
  }

  changePerPage(event) {
    const url = new URL(window.location.href)
    url.searchParams.set("per_page", event.currentTarget.value)
    url.searchParams.delete("page")
    window.location.href = url.toString()
  }

  clearSearch() {
    const searchInput = this.element.querySelector("#search-input")
    if (searchInput) searchInput.value = ""
    this.element.querySelector("form")?.submit()
  }
}
